/*
 * New versions without a manual refresh (owner, Oct 31: "like jobber if I got off it for like 10 seconds if I get back it
 * will reload automatically", and "Tap to update"). Back on the app after 10 seconds or more away: a new version reloads
 * the page; otherwise the screen's data is fetched again, so what is on it is fresh and nothing typed is lost. While the
 * app is open, a new version found on the 5 minute check shows a Tap to update bar instead of reloading under someone.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

const AWAY_MS = 10_000;
const CHECK_MS = 5 * 60_000;

/** The main script this page runs, like "/assets/index-DB6e2ovM.js". Null in development (no built file). */
function scriptOf(html: string): string | null {
  return /\/assets\/index-[\w-]+\.js/.exec(html)?.[0] ?? null;
}
const running = scriptOf(document.documentElement.outerHTML);

/** True when the server now sends a different version than this page runs. */
async function newVersion(): Promise<boolean> {
  if (!running) return false;
  try {
    const r = await fetch("/", { cache: "no-store", credentials: "same-origin" });
    if (!r.ok) return false;
    const now = scriptOf(await r.text());
    return now !== null && now !== running;
  } catch {
    return false;
  }
}

export function UpdateWatch() {
  const qc = useQueryClient();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let leftAt: number | null = document.hidden ? Date.now() : null;
    const onShow = () => {
      if (document.hidden) {
        leftAt = Date.now();
        return;
      }
      const away = leftAt !== null && Date.now() - leftAt >= AWAY_MS;
      leftAt = null;
      if (!away) return;
      void newVersion().then((fresh) => {
        if (fresh) window.location.reload();
        else void qc.invalidateQueries();
      });
    };
    const check = () => {
      if (!document.hidden) void newVersion().then((fresh) => fresh && setReady(true));
    };
    document.addEventListener("visibilitychange", onShow);
    // A phone's home screen app can come back through pageshow without a visibility change.
    window.addEventListener("pageshow", onShow);
    const timer = window.setInterval(check, CHECK_MS);
    return () => {
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("pageshow", onShow);
      window.clearInterval(timer);
    };
  }, [qc]);
  if (!ready) return null;
  return (
    <button type="button" className="update-bar" onClick={() => window.location.reload()}>
      A new version is ready. Tap to update.
    </button>
  );
}
